import { queryOptions, useQuery } from '@tanstack/react-query';
import {
  fetchActorExceptions,
  fetchActors,
  fetchApiKeyUsage,
  fetchBlueprint,
  fetchCatalog,
  fetchResources,
  fetchRole,
  fetchRoles,
  fetchServiceAccounts,
} from '@/api/(admin)/permissions';

/**
 * Every read on this page, keyed by tenant.
 *
 * The tenant is part of every key on purpose: the operator switches companies
 * from the page header, and a key that left it out would show the previous
 * tenant's roles under the new one's name until the refetch landed.
 */
export const permissionsKey = (tenantId: string, ...rest: unknown[]) =>
  ['admin', 'permissions', tenantId, ...rest] as const;

export function rolesQueryOptions(tenantId: string) {
  return queryOptions({
    queryKey: permissionsKey(tenantId, 'roles'),
    queryFn: async () => (await fetchRoles(tenantId)).data,
    staleTime: 60_000,
  });
}

export function actorsQueryOptions(tenantId: string) {
  return queryOptions({
    queryKey: permissionsKey(tenantId, 'actors'),
    queryFn: async () => (await fetchActors(tenantId)).data,
    staleTime: 60_000,
  });
}

export function blueprintQueryOptions(tenantId: string) {
  return queryOptions({
    queryKey: permissionsKey(tenantId, 'blueprint'),
    queryFn: async () => (await fetchBlueprint(tenantId)).data,
    staleTime: 5 * 60_000,
  });
}

export function catalogQueryOptions(tenantId: string) {
  return queryOptions({
    queryKey: permissionsKey(tenantId, 'catalog'),
    queryFn: async () => (await fetchCatalog(tenantId)).data,
    staleTime: 5 * 60_000,
  });
}

export function resourcesQueryOptions(tenantId: string) {
  return queryOptions({
    queryKey: permissionsKey(tenantId, 'resources'),
    queryFn: async () => (await fetchResources(tenantId)).data,
    staleTime: 5 * 60_000,
  });
}

export function serviceAccountsQueryOptions(tenantId: string) {
  return queryOptions({
    queryKey: permissionsKey(tenantId, 'service-accounts'),
    queryFn: async () => (await fetchServiceAccounts(tenantId)).data,
    staleTime: 60_000,
  });
}

export function useRoles(tenantId: string | null) {
  return useQuery({
    ...rolesQueryOptions(tenantId ?? ''),
    enabled: Boolean(tenantId),
  });
}

export function useActors(tenantId: string | null) {
  return useQuery({
    ...actorsQueryOptions(tenantId ?? ''),
    enabled: Boolean(tenantId),
  });
}

/** Drives the permission editor: groups, resources, supported actions, templates. */
export function useBlueprint(tenantId: string | null) {
  return useQuery({
    ...blueprintQueryOptions(tenantId ?? ''),
    enabled: Boolean(tenantId),
  });
}

/** The flat `resource:action` rows — the exceptions form's permission picker. */
export function useCatalog(tenantId: string | null) {
  return useQuery({
    ...catalogQueryOptions(tenantId ?? ''),
    enabled: Boolean(tenantId),
  });
}

export function useResources(tenantId: string | null) {
  return useQuery({
    ...resourcesQueryOptions(tenantId ?? ''),
    enabled: Boolean(tenantId),
  });
}

export function useServiceAccounts(tenantId: string | null) {
  return useQuery({
    ...serviceAccountsQueryOptions(tenantId ?? ''),
    enabled: Boolean(tenantId),
  });
}

/** One role with the permissions granted to it — only once a role is open. */
export function useRoleDetail(tenantId: string | null, key: string | null) {
  return useQuery({
    queryKey: permissionsKey(tenantId ?? '', 'role', key),
    queryFn: async () =>
      (await fetchRole(tenantId as string, key as string)).data,
    enabled: Boolean(tenantId) && Boolean(key),
  });
}

/** A user's per-record exceptions. Service accounts cannot hold any. */
export function useActorExceptions(
  tenantId: string | null,
  userId: string | null,
) {
  return useQuery({
    queryKey: permissionsKey(tenantId ?? '', 'actor-exceptions', userId),
    queryFn: async () =>
      (await fetchActorExceptions(tenantId as string, userId as string)).data,
    enabled: Boolean(tenantId) && Boolean(userId),
  });
}

/** API keys and their request count for one UTC day. */
export function useApiKeyUsage(tenantId: string | null, date: string) {
  return useQuery({
    queryKey: permissionsKey(tenantId ?? '', 'api-key-usage', date),
    queryFn: async () =>
      (await fetchApiKeyUsage(tenantId as string, date)).data,
    enabled: Boolean(tenantId) && Boolean(date),
  });
}
